import { useEffect } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useTelaGrande from '../../../hooks/useTelaGrande';
import useAuth from 'hooks/useAuth';
import { isMaster } from '../../../utils/perfil';
import { NavSectionVertical } from '../../../components/nav-section';
import { navConfigAdmin, navConfigCliente, navConfigAdminPlus } from './NavConfig';
import CollapseButton from './CollapseButton';
import { cn } from '../../../lib/utils';
import { NOVIDADES_VERSION } from '../../../sections/dashboard/novidades/novidadesData';

/* ============================================================================
   Menu lateral - visual novo (shadcn + Tailwind).

   Substitui a versao MUI: dois `Drawer`, `Scrollbar` (simplebar), `cssStyles().
   bgBlur()` e larguras vindas de `config.NAVBAR`. Agora e um `<aside>` fixo no
   desktop e um painel com fundo escurecido no mobile.

   O QUE CONTINUA IGUAL, porque e regra de negocio:
     - a escolha do menu por perfil (Master -> AdminPlus, perfil 0 -> Cliente,
       Admin/Gerente -> Admin), copiada linha a linha;
     - o estado de recolhida vem do CollapseDrawerContext, o mesmo de antes, para
       o conteudo da pagina continuar acompanhando a largura;
     - a gaveta fecha ao trocar de rota no mobile.

   O QUE MUDA, e por que:

   1. LARGURA VEM DO TOKEN `--gps-sidebar-w` (244px), nao de `config.NAVBAR`
      (250px). Passa a existir UMA fonte para a largura do menu, a mesma que o
      restante do visual novo usa; recolhida vira 72px, largura suficiente para o
      icone de 22px respirar sem virar tira.

   2. SAIU O `onMouseEnter/onMouseLeave` que EXPANDIA a barra ao passar o mouse.
      Barra que cresce sozinha empurra o conteudo enquanto a pessoa move o cursor
      para clicar - o alvo foge. O contexto continua expondo os handlers (outros
      lugares leem `collapseHover`), mas a barra so muda por clique no botao.

   3. O rodape da barra mostra o usuario e o perfil. Antes existia `NavbarAccount`
      para isso e estava COMENTADO: quem usa dois logins (o proprio e um de teste)
      nao tinha onde ver em qual estava sem abrir o menu da conta.

   4. Instalar PWA: o codigo antigo guardava `deferredPrompt` numa variavel
      declarada com `let` DENTRO do componente. A cada render ela voltava a
      `undefined`, e o listener gravava nessa copia - o botao "baixar para android"
      cairia sempre no aviso "Verifique se ja foi instalado". Como os tres botoes
      (android/ios/windows) chamavam a MESMA funcao e estavam comentados, nao foram
      reintroduzidos: um botao de instalar que nunca instalou nao e funcionalidade
      perdida. Fica registrado aqui para quem quiser fazer de verdade.
   ============================================================================ */

// Mesma fonte que o rodape usa - versao em UM lugar, nao duas strings para
// divergirem na proxima subida.
const VERSAO = NOVIDADES_VERSION;

const LARGURA_ABERTA = 'var(--gps-sidebar-w)';
const LARGURA_RECOLHIDA = '72px';

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useRouter();
  const { user } = useAuth();
  const isDesktop = useTelaGrande();
  const { isCollapse, collapseClick, onToggleCollapse } = useCollapseDrawer();

  const selectedNavConfig = isMaster(user)
    ? navConfigAdminPlus
    : Number(user?.perfil) === 0
      ? navConfigCliente
      : navConfigAdmin;

  // Fecha a gaveta ao navegar. Sem isso, no celular a pagina trocava atras de um
  // menu que continuava aberto em cima dela.
  useEffect(() => {
    if (isOpenSidebar) onCloseSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Esc fecha a gaveta - o menu ocupa a tela toda no celular.
  useEffect(() => {
    if (!isOpenSidebar) return;
    const aoTeclar = (e) => e.key === 'Escape' && onCloseSidebar();
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [isOpenSidebar, onCloseSidebar]);

  const perfilTexto =
    Number(user?.perfil) === 3
      ? 'Master'
      : Number(user?.perfil) === 1
        ? 'Administrador'
        : Number(user?.perfil) === 2
          ? 'Gerente'
          : 'Usuário';

  const conteudo = (
    <div className="flex h-full flex-col">
      {/* Topo da barra: a marca mora AQUI, nao no topbar.
          Logo na lateral e a convencao do app-shell moderno e libera a faixa de
          cima para o que muda por tela.

          A FAIXA DA COR PRIMARY ATRAVESSA (19/08/2026, a pedido): este bloco tem
          exatamente `--gps-topbar-h` de altura e agora a mesma cor do topbar, que
          comeca onde a sidebar termina. Os dois encostam e leem como UMA faixa
          continua de ponta a ponta da janela - antes havia um retangulo branco no
          canto esquerdo interrompendo a barra.

          O LOGO VOLTOU A SER O BRANCO (`gps_hub2.png`). Eu havia trocado para o
          `gps_hub.png` (colorido, com o "hub" em texto escuro) porque a superficie
          aqui era neutra; sobre a faixa escura aquele texto escuro fica ilegivel.
          Os dois arquivos tem 745x287 - e a mesma arte em duas versoes, nada foi
          redesenhado. */}
      <div
        className="flex flex-none items-center gap-2 border-b border-primary bg-primary px-2 text-primary-foreground"
        style={{ height: 'var(--gps-topbar-h)' }}
      >
        <NextLink
          href="/dashboard/app"
          className="flex min-w-0 items-center gap-2"
          title={`GPShub ${VERSAO}`}
        >
          {/* A IMAGEM JA E A MARCA ESCRITA: gps_hub.png tem 745x287 (proporcao
              2,6) e traz o nome "GPS hub" desenhado dentro. Escrever "GPShub" ao
              lado repetia o nome duas vezes.

              E o mais importante: a imagem estava com `h-7 w-7`, ou seja
              ESPREMIDA num quadrado de 28px - uma marca de proporcao 2,6 forcada
              a 1:1 fica ilegivel, que era o aspecto apertado da lateral.
              `w-auto` devolve a proporcao. */}
          {/* 🔴 DEFEITO CORRIGIDO (26/08/2026): "esta minimizado, o icone some".

              Recolhida, a barra tem 72px. Descontando o `px-2` sobram 56px, e o
              botao de recolher ao lado ocupa ~28px - restam ~28px para a marca. O
              `gps_hub2.png` tem proporcao 2,6 (745x287): mesmo em `h-5` ele pede
              ~52px de largura, quase o dobro do que ha. O resultado era a marca
              espremida e cortada.

              Recolhida entra o ICONE QUADRADO do produto (o mesmo favicon), que
              cabe em 28px sem deformar. Nao e um logo diferente: e a mesma marca na
              versao que existe para espaco quadrado - trocar a arte por um
              retangulo forcado a caber seria pior que trocar de arquivo. */}
          {isDesktop && isCollapse ? (
            <img
              src="/favicon/favicon-32x32.png"
              alt={`GPShub ${VERSAO}`}
              className="h-6 w-6 flex-none rounded object-contain"
            />
          ) : (
            <img src="/images/gps_hub2.png" alt={`GPShub ${VERSAO}`} className="h-7 w-auto flex-none object-contain" />
          )}

          {/* Recolhida cabe a marca menor, mas nao a versao: 72px de largura nao
              acomodam as duas sem cortar uma. A versao continua no title do link
              e no rodape. */}
          {!(isDesktop && isCollapse) && (
            /* A VERSAO fica aqui, ao lado da marca, e nao so no rodape: e o
               primeiro dado que o suporte pede, e o rodape exige rolar a pagina
               toda. Em mono tabular porque e identificador, nao texto - e alinha
               quando muda de 1.1 para 1.10. */
            <span className="flex-none rounded bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-primary-foreground">
              {VERSAO}
            </span>
          )}
        </NextLink>

        <div className="ml-auto">
          {isDesktop ? (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          ) : (
            <button
              type="button"
              onClick={onCloseSidebar}
              aria-label="Fechar menu"
              className="grid h-7 w-7 place-items-center rounded-md border border-primary-foreground/40 text-primary-foreground"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto border-r border-line py-1">
        <NavSectionVertical navConfig={selectedNavConfig} isCollapse={isDesktop && isCollapse} />
      </div>

      <div className="flex-none border-r border-t border-line p-2">
        {isDesktop && isCollapse ? (
          <div
            className="mx-auto grid h-8 w-8 place-items-center rounded-md bg-soft text-[11px] font-extrabold text-muted-foreground"
            title={`${user?.nome || ''} · ${perfilTexto}`}
          >
            {String(user?.nome || '?').trim().charAt(0).toUpperCase()}
          </div>
        ) : (
          <div className="rounded-lg bg-soft px-2.5 py-2">
            <div className="truncate text-[12px] font-bold">{user?.nome || '—'}</div>
            <div className="truncate text-[11px] text-muted-foreground">{perfilTexto}</div>
          </div>
        )}
      </div>
    </div>
  );

  if (!isDesktop) {
    return (
      <>
        {isOpenSidebar && (
          <div className="fixed inset-0 z-[1200] bg-black/40" onClick={onCloseSidebar} aria-hidden />
        )}
        <aside
          className={`fixed left-0 top-0 z-[1250] h-full bg-surface transition-transform duration-200 ${
            isOpenSidebar ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{ width: LARGURA_ABERTA }}
          aria-hidden={!isOpenSidebar}
        >
          {conteudo}
        </aside>
      </>
    );
  }

  return (
    <aside
      /* SEM `border-r` no <aside>.
         A borda cinza percorria a altura TODA, inclusive a faixa colorida do topo -
         e uma linha cinza de 1px atravessando a faixa quebra justamente a
         continuidade que ela deveria ter com o topbar. A borda passou para os dois
         blocos de baixo (menu e cartao do usuario), que sao os que precisam se
         separar do conteudo. */
      className="fixed left-0 top-0 z-[1150] h-full bg-surface transition-[width] duration-200"
      style={{ width: isCollapse ? LARGURA_RECOLHIDA : LARGURA_ABERTA }}
    >
      {conteudo}
    </aside>
  );
}
